import { uiPaper } from "shared/ui/uiSurface"
import { Close, Delete, Edit, Save } from "@mui/icons-material"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useGetAddressesQuery,
  useUpdateAddressMutation,
  useDeleteAddressMutation,
} from "../../apis/appApi"
import type { AddressListItem, AddressUpdateInput } from "../../apis/appApi"

interface AddressDetailDrawerProps {
  addressId: number | null
  open: boolean
  onClose: () => void
  onSuccess: (message: string) => void
}

const MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ""
const DRAWER_WIDTH = 420

export const AddressDetailDrawer: FC<AddressDetailDrawerProps> = ({
  addressId,
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [updateAddress] = useUpdateAddressMutation()
  const [deleteAddress] = useDeleteAddressMutation()

  // Find the address from the cached query results
  const { data: addressesData } = useGetAddressesQuery(
    { page: 1, pageSize: 100 },
    { skip: !addressId },
  )
  const address = addressesData?.data?.find(
    (a: AddressListItem) => a.id === addressId,
  )

  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<AddressUpdateInput>({})
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  useEffect(() => {
    if (address) {
      setForm({
        streetNumber: address.streetNumber ?? "",
        streetName: address.streetName ?? "",
        appartNumber: address.appartNumber,
        city: address.city ?? "",
        state: address.state,
        country: address.country ?? "",
        postcode: address.postcode ?? "",
        label: address.label,
        notes: address.notes,
      })
    }
    setEditing(false)
  }, [address])

  const handleSave = async () => {
    if (!addressId) return
    try {
      await updateAddress({ id: addressId, body: form }).unwrap()
      onSuccess(t("addresses.detail.updated"))
      setEditing(false)
    } catch {
      // Error handled by RTK Query
    }
  }

  const handleDelete = async () => {
    if (!addressId) return
    setDeleteDialogOpen(false)
    try {
      await deleteAddress(addressId).unwrap()
      onSuccess(t("addresses.detail.deleted"))
      onClose()
    } catch {
      // Error handled by RTK Query
    }
  }

  const clientName = address
    ? address.clientOrganisation ||
      [address.clientFirstName, address.clientLastName]
        .filter(Boolean)
        .join(" ") ||
      null
    : null

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ ...uiPaper("UI-DLV2-152"), sx: { width: DRAWER_WIDTH } }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Typography variant="h6" fontWeight={600}>
            {t("addresses.detail.title")}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>

        {address && (
          <Box sx={{ p: 2, overflow: "auto", flex: 1 }}>
            {/* Map Preview */}
            {address.latitude != null && address.longitude != null && MAPS_API_KEY ? (
              <Box sx={{ mb: 2, borderRadius: 1, overflow: "hidden", height: 200 }}>
                <APIProvider apiKey={MAPS_API_KEY}>
                  <Map
                    defaultCenter={{ lat: address.latitude, lng: address.longitude }}
                    defaultZoom={15}
                    gestureHandling="cooperative"
                    disableDefaultUI
                    mapId="address-detail-map"
                    style={{ width: "100%", height: "100%" }}
                  >
                    <AdvancedMarker
                      position={{ lat: address.latitude, lng: address.longitude }}
                    />
                  </Map>
                </APIProvider>
              </Box>
            ) : (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 2, display: "block", fontStyle: "italic" }}
              >
                {t("addresses.detail.notGeocoded")}
              </Typography>
            )}

            {/* Linked Client */}
            {clientName && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  {t("addresses.detail.linkedClients")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {clientName}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </>
            )}

            {/* Address Fields */}
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <TextField
                  label={t("addresses.streetNumber")}
                  size="small"
                  value={form.streetNumber ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, streetNumber: e.target.value }))
                  }
                  disabled={!editing}
                  sx={{ flex: 0.4 }}
                />
                <TextField
                  label={t("addresses.streetName")}
                  size="small"
                  value={form.streetName ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, streetName: e.target.value }))
                  }
                  disabled={!editing}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                label={t("addresses.apt")}
                size="small"
                value={form.appartNumber ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, appartNumber: e.target.value || null }))
                }
                disabled={!editing}
              />
              <Stack direction="row" spacing={1}>
                <TextField
                  label={t("addresses.city")}
                  size="small"
                  value={form.city ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, city: e.target.value }))
                  }
                  disabled={!editing}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={t("addresses.state")}
                  size="small"
                  value={form.state ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, state: e.target.value || null }))
                  }
                  disabled={!editing}
                  sx={{ flex: 0.8 }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  label={t("addresses.postcode")}
                  size="small"
                  value={form.postcode ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, postcode: e.target.value }))
                  }
                  disabled={!editing}
                  sx={{ flex: 0.6 }}
                />
                <TextField
                  label={t("addresses.country")}
                  size="small"
                  value={form.country ?? ""}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, country: e.target.value }))
                  }
                  disabled={!editing}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                label={t("addresses.label")}
                size="small"
                value={form.label ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, label: e.target.value || null }))
                }
                disabled={!editing}
              />
              <TextField
                label={t("addresses.notes")}
                size="small"
                value={form.notes ?? ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, notes: e.target.value || null }))
                }
                disabled={!editing}
                multiline
                rows={3}
              />
            </Stack>
          </Box>
        )}

        {/* Actions */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ p: 2, borderTop: 1, borderColor: "divider" }}
        >
          {editing ? (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ textTransform: "none" }}
              >
                {t("common.save")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setEditing(false)}
                sx={{ textTransform: "none" }}
              >
                {t("common.cancel")}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Edit />}
                onClick={() => setEditing(true)}
                sx={{ textTransform: "none" }}
              >
                {t("addresses.detail.edit")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ textTransform: "none" }}
              >
                {t("addresses.detail.delete")}
              </Button>
            </>
          )}
        </Stack>
      </Drawer>

      {/* Delete confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-153") }}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("addresses.detail.delete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("addresses.detail.deleteConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
